import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/lib/auth";
import { authorBadge } from "@/lib/membership";

/**
 * 커뮤니티 — kangstudy_posts/comments/likes.
 * 읽기는 공개(뷰로 카운트 포함), 쓰기는 로그인 회원 본인만(RLS 강제).
 * 목업 필드명(camelCase)에 맞춰 매핑한다.
 */
export interface CommunityComment {
  id: string;
  author: string;
  authorBadge?: string | null;
  content: string;
  createdAt: string;
}
export interface CommunityPost {
  id: string;
  title: string;
  content: string;
  category: string;
  author: string;
  authorBadge?: string | null;
  authorId: string;
  createdAt: string;
  views: number;
  likes: number;
  commentCount: number;
  thumbnail?: string | null;
  isBest: boolean;
  pinned: boolean;
  resolved: boolean;
  tags: string[];
}

function toPost(r: Record<string, unknown>): CommunityPost {
  return {
    id: String(r.id),
    title: (r.title as string) || "",
    content: (r.content as string) || "",
    category: (r.category as string) || "",
    author: (r.author_name as string) || "",
    authorBadge: (r.author_badge as string) ?? null,
    authorId: (r.author_id as string) || "",
    createdAt: ((r.created_at as string) || "").slice(0, 10),
    views: Number(r.views) || 0,
    likes: Number(r.like_count) || 0,
    commentCount: Number(r.comment_count) || 0,
    thumbnail: (r.thumbnail as string) ?? null,
    isBest: Boolean(r.is_best),
    pinned: Boolean(r.pinned),
    resolved: Boolean(r.resolved),
    tags: Array.isArray(r.tags) ? (r.tags as string[]) : [],
  };
}

export function useCommunity() {
  const { user } = useAuth();
  const [posts, setPosts] = useState<CommunityPost[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    // 공지·베스트가 위로, 그다음 최신순
    const { data, error } = await supabase
      .from("kangstudy_posts_with_counts")
      .select("*")
      .order("pinned", { ascending: false })
      .order("created_at", { ascending: false });
    if (!error && data) setPosts(data.map((r) => toPost(r as Record<string, unknown>)));
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  /** 글 작성 — 로그인 필수. author_name/badge 는 판정 정본에서 파생. */
  const createPost = useCallback(
    async (input: { title: string; content: string; category: string; tags?: string[] }): Promise<{ ok: boolean; error?: string }> => {
      if (!user) return { ok: false, error: "로그인이 필요합니다." };
      const name =
        (user.user_metadata?.name as string) || user.email?.split("@")[0] || "회원";
      const { error } = await supabase.from("kangstudy_posts").insert({
        author_id: user.id,
        author_name: name,
        author_badge: authorBadge(user),
        title: input.title,
        content: input.content,
        category: input.category,
        tags: input.tags ?? [],
      });
      if (error) {
        // 공지 카테고리 등 RLS 위반은 한국어로
        const msg = /row-level security|violates/i.test(error.message)
          ? "이 게시판에는 글을 쓸 수 없습니다."
          : "글 등록에 실패했습니다. 다시 시도해주세요.";
        return { ok: false, error: msg };
      }
      await load();
      return { ok: true };
    },
    [user, load],
  );

  /** 좋아요 토글 — 회원당 글당 1개(PK 유니크). */
  const toggleLike = useCallback(
    async (postId: string): Promise<boolean> => {
      if (!user) return false;
      const { data: existing } = await supabase
        .from("kangstudy_post_likes")
        .select("post_id")
        .eq("post_id", postId)
        .eq("user_id", user.id)
        .maybeSingle();
      if (existing) {
        await supabase.from("kangstudy_post_likes").delete().eq("post_id", postId).eq("user_id", user.id);
      } else {
        await supabase.from("kangstudy_post_likes").insert({ post_id: postId, user_id: user.id });
      }
      await load();
      return true;
    },
    [user, load],
  );

  /** 조회수 +1 (익명 포함). */
  const bumpView = useCallback(async (postId: string) => {
    await supabase.rpc("kangstudy_bump_post_view", { p_id: Number(postId) });
  }, []);

  return { posts, loading, createPost, toggleLike, bumpView, refetch: load, canWrite: !!user };
}

/** 게시글 하나의 댓글. */
export function usePostComments(postId: string | null) {
  const { user } = useAuth();
  const [comments, setComments] = useState<CommunityComment[]>([]);

  const load = useCallback(async () => {
    if (!postId) return;
    const { data } = await supabase
      .from("kangstudy_comments")
      .select("*")
      .eq("post_id", postId)
      .order("created_at", { ascending: true });
    setComments(
      (data ?? []).map((c: Record<string, unknown>) => ({
        id: String(c.id),
        author: (c.author_name as string) || "",
        authorBadge: (c.author_badge as string) ?? null,
        content: (c.content as string) || "",
        createdAt: ((c.created_at as string) || "").slice(0, 10),
      })),
    );
  }, [postId]);

  useEffect(() => { load(); }, [load]);

  const addComment = useCallback(
    async (content: string): Promise<{ ok: boolean; error?: string }> => {
      if (!user) return { ok: false, error: "로그인이 필요합니다." };
      if (!postId) return { ok: false, error: "게시글을 찾을 수 없습니다." };
      const name =
        (user.user_metadata?.name as string) || user.email?.split("@")[0] || "회원";
      const { error } = await supabase.from("kangstudy_comments").insert({
        post_id: postId,
        author_id: user.id,
        author_name: name,
        author_badge: authorBadge(user),
        content,
      });
      if (error) return { ok: false, error: "댓글 등록에 실패했습니다." };
      await load();
      return { ok: true };
    },
    [user, postId, load],
  );

  return { comments, addComment, refetch: load };
}
